import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Save, Loader2 } from "lucide-react";
import { SettingControl } from "./SettingControl";
import { useAppSettings } from "@/hooks/useAppSettings";

interface AppSetting {
  id: string;
  key: string;
  value: any;
  type: 'boolean' | 'string' | 'number' | 'json' | 'date';
  category: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface SettingsCategoryProps {
  category: string;
  settings: AppSetting[];
  title: string;
  description: string;
}

export function SettingsCategory({ category, settings, title, description }: SettingsCategoryProps) {
  const [localValues, setLocalValues] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { updateSetting, refetch } = useAppSettings();

  // Actualizar valores locales cuando cambien las configuraciones
  useEffect(() => {
    if (settings.length === 0) return;
    
    const newValues: Record<string, any> = {};
    settings.forEach(setting => {
      newValues[setting.key] = setting.value;
    });
    
    console.log('🔄 Settings changed for category', category, ':', {
      previousValues: localValues,
      newValues,
      isInitialized,
      isSaving
    });
    
    // SIMPLIFICADO: Solo inicializar una vez o cuando NO estamos guardando
    if (!isInitialized) {
      setLocalValues(newValues);
      setIsInitialized(true);
      console.log('✅ Category initialized with values:', newValues);
    }
    // NO actualizar automáticamente mientras hay cambios pendientes o estamos guardando
  }, [settings, category]);

  // Check if there are any changes
  const hasChanges = settings.some(setting => {
    const localValue = localValues[setting.key];
    const settingValue = setting.value;
    const isChanged = localValue !== settingValue;
    
    if (category === 'attendance') {
      console.log('🔍 Change detection for', setting.key, ':', {
        localValue,
        settingValue,
        localType: typeof localValue,
        settingType: typeof settingValue,
        isChanged,
        strictEquality: localValue === settingValue,
        looseEquality: localValue == settingValue
      });
    }
    
    return isChanged;
  });

  if (category === 'attendance') {
    console.log('🎯 Has changes result:', hasChanges, 'for category:', category);
  }

  const handleValueChange = (key: string, value: any) => {
    setLocalValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveAll = async () => {
    if (!hasChanges) return;
    
    setIsSaving(true);
    try {
      console.log('💾 Guardando configuraciones cambiadas:', 
        settings
          .filter(setting => localValues[setting.key] !== setting.value)
          .map(s => ({ key: s.key, oldValue: s.value, newValue: localValues[s.key] }))
      );

      // Update all changed settings
      const promises = settings
        .filter(setting => localValues[setting.key] !== setting.value)
        .map(setting => updateSetting(setting.key, localValues[setting.key]));
      
      await Promise.all(promises);
      
      console.log('✅ Todas las configuraciones guardadas correctamente');
      
      // ACTUALIZAR EL ESTADO LOCAL DIRECTAMENTE CON LOS VALORES GUARDADOS
      const updatedValues = { ...localValues };
      settings
        .filter(setting => localValues[setting.key] !== setting.value)
        .forEach(setting => {
          updatedValues[setting.key] = localValues[setting.key]; // Usar el valor que acabamos de guardar
        });
      
      console.log('🔄 Actualizando estado local con valores guardados:', updatedValues);
      
      // Hacer refetch en background para mantener sincronización
      setTimeout(async () => {
        await refetch();
        if (category === 'enrollment') {
          window.dispatchEvent(new CustomEvent('coursesUpdated'));
        }
      }, 100);
      
    } catch (error) {
      console.error('❌ Error guardando configuraciones:', error);
      // Error handling is done in the hook, reset to original values
      const resetValues: Record<string, any> = {};
      settings.forEach(setting => {
        resetValues[setting.key] = setting.value;
      });
      setLocalValues(resetValues);
    } finally {
      setIsSaving(false);
    }
  };

  if (settings.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground text-center">
            No hay configuraciones disponibles en esta categoría.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {settings.map((setting) => (
          <SettingControl
            key={setting.id}
            setting={setting}
            value={localValues[setting.key]}
            onValueChange={(value) => handleValueChange(setting.key, value)}
            disabled={isSaving}
          />
        ))}
        
        {hasChanges && (
          <div className="pt-4 border-t">
            <Button 
              onClick={handleSaveAll} 
              disabled={isSaving}
              className="w-full"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando cambios...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Guardar todos los cambios
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}